// export default AudioPlayerQueueComponent;
/* eslint-disable jsx-a11y/click-events-have-key-events */
/* eslint-disable jsx-a11y/no-static-element-interactions */
import { DndContext, DragEndEvent, PointerSensor, TouchSensor, closestCorners, useSensor, useSensors } from "@dnd-kit/core";
import { SortableContext, useSortable, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { useSearchParams } from "react-router";
import { memo, useEffect, useRef } from "react";
import { MdDragIndicator, MdOutlineClose } from "react-icons/md";
import "react-swipeable-list/dist/styles.css";
import { useFontStyle } from "~/hook/useFontStyle";
import { AudioPlayerProps } from "~/interface/audioplayer/audioplayer";
import { addAudioQueue, addToCurrentAudio, changeIsPlayingState, setCurrentAudioTime, setProgress } from "~/lib/store/slice/audioSlice";
import { useAppDispatch, useAppSelector } from "~/lib/store/store";
import { cn } from "~/lib/utils";
import { AUDIO_PLAYER, COLORS } from "~/utils/constant";
import NodataFound from "../shared-components/NoDataFound";

interface QueueCardsProps {
  item: AudioPlayerProps;
  handleRemoveAudioFromQueue: (id: number) => void;
  handleChangeAudio: (item: AudioPlayerProps) => void;
  currentAudio: AudioPlayerProps | null;
}

const AudioPlayerQueueComponent = () => {
  const dispatch = useAppDispatch();
  const [searchParams, setSearchParams] = useSearchParams();
  const { audioQueue, currentAudio } = useAppSelector((state) => state.nityNiyam);
  const queueContainerRef = useRef<HTMLDivElement>(null);

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      const oldIndex = audioQueue?.findIndex((item) => item.id === active.id);
      const newIndex = audioQueue?.findIndex((item) => item.id === over.id);
      if (oldIndex !== undefined && newIndex !== undefined && oldIndex !== -1 && newIndex !== -1 && audioQueue) {
        const newItems = [...audioQueue];
        const [movedItem] = newItems.splice(oldIndex, 1);
        newItems.splice(newIndex, 0, movedItem);
        dispatch(addAudioQueue(newItems));
      }
    }
  };

  const handleRemoveAudioFromQueue = (id: number) => {
    const audioQueueFromSession = JSON.parse(sessionStorage.getItem(AUDIO_PLAYER.AUDIO_QUEUE) || "[]");
    const newItems = audioQueueFromSession?.filter((item: AudioPlayerProps) => Number(item.id) !== id);
    dispatch(addAudioQueue(newItems ? newItems : null));
  };

  const handleChabgeAudio = (item: AudioPlayerProps) => {
    const type = searchParams.get("media");
    if (type === "audio") {
      setSearchParams(
        (prev) => {
          prev.set("watch", String(item.id));
          return prev;
        },
        { replace: true },
      );
    }

    dispatch(addToCurrentAudio(item));
    dispatch(setProgress(0));
    dispatch(setCurrentAudioTime(0));
    dispatch(changeIsPlayingState(true));
    sessionStorage.setItem(AUDIO_PLAYER.CURRENT_AUDIO, JSON.stringify(item));
  };

  const sensons = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
        delay: 100,
      },
    }),
    useSensor(TouchSensor, {
      activationConstraint: {
        delay: 200,
        tolerance: 5,
      },
    }),
  );

  useEffect(() => {
    if (currentAudio?.id) {
      const currentElement = queueContainerRef.current?.querySelector(`[data-audio-id="${currentAudio.id}"]`) as HTMLElement;

      if (currentElement) {
        setTimeout(() => {
          currentElement.scrollIntoView({
            behavior: "smooth",
            block: "nearest",
          });
        }, 100);
      }
    }
  }, [currentAudio?.id]);

  return (
    <div className="w-full flex flex-col justify-start items-center gap-3 mb-2">
      <div
        ref={queueContainerRef}
        className="w-full flex-col justify-start items-center overflow-hidden"
        style={{ touchAction: "pan-y" }}
        onClick={(e) => {
          e.stopPropagation();
        }}
      >
        {audioQueue && audioQueue.length > 0 ? (
          <DndContext collisionDetection={closestCorners} onDragEnd={handleDragEnd} sensors={sensons}>
            <SortableContext items={audioQueue} strategy={verticalListSortingStrategy}>
              {audioQueue.map((item) => (
                <QueCards
                  key={item.id}
                  item={item}
                  handleRemoveAudioFromQueue={handleRemoveAudioFromQueue}
                  handleChangeAudio={handleChabgeAudio}
                  currentAudio={currentAudio}
                />
              ))}
            </SortableContext>
          </DndContext>
        ) : (
          <div className="w-full flex justify-center items-center py-10">
            <NodataFound text="No Queue availbale" />
          </div>
        )}
      </div>
    </div>
  );
};

const QueCards = ({ item, handleRemoveAudioFromQueue, handleChangeAudio, currentAudio }: QueueCardsProps) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: item.id });
  const { normalInter } = useFontStyle();
  const isMouseDownRef = useRef(false);
  const dragStartTimeRef = useRef<number>(0);
  const touchStartYRef = useRef<number>(0);
  const touchStartXRef = useRef<number>(0);
  const isScrollingRef = useRef<boolean>(false);
  const isDragEnabledRef = useRef<boolean>(false);
  const isHoldingRef = useRef<boolean>(false);
  const dragActivationTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const style = {
    transform: CSS.Transform.toString(transform),
    transition: transition || "",
    opacity: isDragging ? 0.8 : 1,
    willChange: isDragging ? "transform" : "auto",
    zIndex: isDragging ? 1000 : "auto",
  };

  const clearDragTimeout = () => {
    if (dragActivationTimeoutRef.current) {
      clearTimeout(dragActivationTimeoutRef.current);
      dragActivationTimeoutRef.current = null;
    }
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    if (isDragging) return;

    const touch = e.touches?.[0];
    if (!touch) return;

    dragStartTimeRef.current = Date.now();
    touchStartYRef.current = touch.clientY;
    touchStartXRef.current = touch.clientX;
    isMouseDownRef.current = true;
    isScrollingRef.current = false;
    isDragEnabledRef.current = false;
    isHoldingRef.current = true;

    // Set timeout for drag activation after 200ms hold - MUST MATCH TouchSensor delay
    dragActivationTimeoutRef.current = setTimeout(() => {
      isDragEnabledRef.current = true;
      // After 200ms, we're in drag mode - block all default behaviors
    }, 0);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    const touch = e.touches?.[0];
    if (!touch || !isMouseDownRef.current) return;

    const now = Date.now();
    const holdDuration = now - dragStartTimeRef.current;
    const deltaY = Math.abs(touch.clientY - touchStartYRef.current);

    // If user moved >5px vertically within 200ms, it's scrolling - cancel drag timeout
    if (holdDuration < 200 && deltaY > 5) {
      clearDragTimeout();
      isScrollingRef.current = true;
      isDragEnabledRef.current = false;
      return; // Allow natural scroll
    }

    // Once we're past 200ms hold OR actively dragging, always prevent default
    if (holdDuration >= 200 || isDragging || isDragEnabledRef.current) {
      e.preventDefault(); // Block pull-to-refresh and scrolling during drag
    }
  };

  const handleTouchEnd = () => {
    clearDragTimeout();
    isMouseDownRef.current = false;
    isDragEnabledRef.current = false;
    isHoldingRef.current = false;
  };

  const handleMouseDown = (e: React.MouseEvent | React.TouchEvent) => {
    // Only handle touch events with this - mouse clicks are handled separately
    if (!isDragging && "touches" in e && e.touches) {
      handleTouchStart(e as React.TouchEvent);
    }
  };

  return (
    <div
      ref={setNodeRef}
      data-audio-id={item.id}
      className={cn(
        "w-full bg-secondaryBackground py-1 border-b-[1px] border-primaryColor border-opacity-[15%] flex justify-between items-center select-none overflow-hidden",
        currentAudio?.id === item.id ? "bg-textHeadingColor" : "bg-secondaryBackground",
      )}
      style={{ ...style, touchAction: isDragEnabledRef.current || isDragging ? "none" : "pan-y" }}
    >
      <div className="w-full h-full">
        <div
          className="flex justify-between items-center w-full px-2"
          onClick={(e) => {
            // Allow clicks when not dragging
            if (!isDragging) {
              e.stopPropagation();
              handleChangeAudio(item);
            }
          }}
        >
          <div className="flex items-center gap-x-3">
            <div
              className={cn(
                "cursor-move transition-all duration-150 touch-none select-none",
                isDragging ? "opacity-100 scale-110" : isHoldingRef.current ? "opacity-80 scale-105" : "opacity-60 hover:opacity-80 hover:scale-105",
              )}
              title="Hold to drag on mobile"
              style={{ willChange: isDragging ? "opacity, transform" : "auto" }}
              onMouseDown={handleMouseDown}
              onTouchStart={handleTouchStart}
              onTouchMove={handleTouchMove}
              onTouchEnd={handleTouchEnd}
              {...listeners}
              {...attributes}
            >
              <MdDragIndicator size={20} color={currentAudio?.id === item.id ? COLORS.white : COLORS.iconOrange} />
            </div>
            <div className="size-[35px] w-[40px]">
              <img
                src={item.img ? item.img : "./images/audioPlayerThumbnail.png"}
                alt="audio_thumbnail"
                className="w-full h-full object-contain select-none"
              />
            </div>
            <div className="flex flex-col justify-start items-start xs:w-[150px] smd:w-[230px] 2xl:w-[230px]">
              <span
                className={cn(
                  "xs:text-14 md:text-14 w-full truncate",
                  normalInter,
                  currentAudio?.id === item.id ? "text-white" : "text-textHeadingColor",
                )}
              >
                {item.name}
              </span>
              <span className={cn("xs:text-12 md:text-14 text-textSecondaryColor w-full truncate", normalInter)}>{item.artist}</span>
            </div>
          </div>
          {currentAudio?.id !== item.id && (
            <div
              className="cursor-pointer size-[18px] border-[1px] border-primaryColor bg-primary rounded-full flex justify-center items-center hover:bg-textHeadingColor group-hover:text-white"
              title="remove from queue"
              onMouseDown={(e) => {
                e.stopPropagation();
                handleRemoveAudioFromQueue(Number(item.id));
              }}
            >
              <MdOutlineClose size={12} className={cn("opacity-100 text-textHeadingColor hover:text-white select-none")} />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default memo(AudioPlayerQueueComponent);
